import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, apiError } from "@/lib/api";
import { offerKeyFromUrl } from "@/config/branding";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/Markdown";
import { MiraAvatar } from "@/components/MiraAvatar";
import { toast } from "sonner";
import { ArrowUp, Loader2 } from "lucide-react";

const GREETING =
  "Hi there! Before we get started, where should I send your growth plan when it's ready? Just drop your email below 👇";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// SHA-256 hex — privacy-safe hashed email for the ChatGPT Ads pixel.
async function sha256Hex(str) {
  try {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

async function fireLeadConversion(email, eventId) {
  if (typeof window === "undefined" || !window.oaiq) return;
  try {
    const email_sha256 = await sha256Hex(email.trim().toLowerCase());
    if (email_sha256) window.oaiq("init", { user: { email_sha256 } });
    window.oaiq("measure", "lead_created", { type: "customer_action" }, { event_id: eventId });
  } catch {
    /* never block the flow on tracking */
  }
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 py-1" data-testid="mira-typing">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-2 w-2 rounded-full bg-slate-400 animate-bounce"
          style={{ animationDelay: `${i * 0.18}s`, animationDuration: "1s" }}
        />
      ))}
    </div>
  );
}

// The full growth-plan conversation runs here as a simple, standalone chat — no logged-in app
// chrome. Email gate first, then Mira's questionnaire → diagnosis → plan, all inline.
export default function ChatPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [phase, setPhase] = useState("email"); // "email" | "chat"
  const [input, setInput] = useState("");
  const [thread, setThread] = useState([]); // {role, content}
  const [typing, setTyping] = useState(true);
  const [loading, setLoading] = useState(false); // email submitting
  const [streaming, setStreaming] = useState(false);
  const [convId, setConvId] = useState(null);
  const endRef = useRef(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const started = useRef(false);

  // Resume an existing conversation (?c=) in simple chat mode.
  useEffect(() => {
    const c = params.get("c");
    if (!c || started.current) return;
    started.current = true;
    setParams({}, { replace: true });
    setPhase("chat");
    setTyping(false);
    setConvId(c);
    api
      .get(`/conversations/${c}/messages`)
      .then(({ data }) => setThread(data.map((m) => ({ role: m.role, content: m.content }))))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Fresh visit: Mira "types" for a beat, then shows her greeting + the email gate.
  useEffect(() => {
    if (params.get("c")) return;
    const t = setTimeout(() => {
      setThread([{ role: "assistant", content: GREETING }]);
      setTyping(false);
    }, 1400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);

  const scrollEnd = (smooth = true) =>
    endRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "end" });

  useEffect(() => {
    scrollEnd();
  }, [thread, typing, loading, streaming]);

  // iMessage-style keyboard handling: size the chat to the *visible* viewport (which shrinks
  // when the software keyboard opens) so the input never hides behind it, and keep the latest
  // message pinned right above the input.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const apply = () => {
      const root = rootRef.current;
      if (root) {
        root.style.height = `${vv.height}px`;
        root.style.transform = `translateY(${vv.offsetTop}px)`;
      }
      scrollEnd(false);
    };
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
    };
    // eslint-disable-next-line
  }, []);

  const submitEmail = async () => {
    const email = input.trim();
    if (!email || loading || typing) return;
    setThread((t) => [...t, { role: "user", content: email }]);
    setInput("");
    inputRef.current?.focus(); // keep the box active (keyboard open) into the chat
    if (!EMAIL_RE.test(email)) {
      setThread((t) => [
        ...t,
        { role: "assistant", content: "Hmm, that doesn't look like an email — mind popping in a valid one so I know where to send your plan?" },
      ]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/anon/start", { email });
      if (data.exists) {
        setThread((t) => [
          ...t,
          { role: "assistant", content: "Looks like you already have an account with that email! Log in and we'll pick your plan right back up." },
        ]);
        toast.info("That email already has an account — log in to continue.");
        setTimeout(() => navigate("/login", { state: { email } }), 1400);
        return;
      }
      await fireLeadConversion(email, data.user_id);
      setConvId(data.conversation_id);
      // Load the seeded opening (greeting, email, name question) and keep chatting inline —
      // no redirect into the logged-in app.
      const { data: msgs } = await api.get(`/conversations/${data.conversation_id}/messages`);
      setThread(msgs.map((m) => ({ role: m.role, content: m.content })));
      setPhase("chat");
      inputRef.current?.focus();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const trackOffer = async (url) => {
    try { await api.post("/cta/click", { cta_type: offerKeyFromUrl(url), context: convId }); } catch {}
  };

  const sendMessage = useCallback(async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    // Keep the input focused so the keyboard stays open after sending (iMessage/ChatGPT style).
    inputRef.current?.focus();
    setThread((t) => [...t, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, conversation_id: convId, persona: "growth" }),
      });
      if (!res.ok || !res.body) throw new Error("Mira is unavailable");
      const newCid = res.headers.get("X-Conversation-Id");
      if (newCid && !convId) setConvId(newCid);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setThread((t) => {
          const c = [...t];
          c[c.length - 1] = { role: "assistant", content: acc };
          return c;
        });
      }
    } catch (e) {
      toast.error(apiError(e.message));
      setThread((t) => {
        const c = [...t];
        c[c.length - 1] = { role: "assistant", content: "Sorry — I hit an error. Please try again." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, convId]);

  const onSubmit = () => { if (phase === "email") submitEmail(); else sendMessage(); };
  const busy = loading || streaming;

  return (
    <div ref={rootRef} className="fixed inset-x-0 top-0 [height:100dvh] overflow-hidden bg-background mm-grid-bg text-slate-100 flex flex-col" data-testid="chat-page">
      {/* Slim Mira header — the only chrome */}
      <header className="flex items-center gap-3 px-5 py-3 border-b border-slate-800/80 bg-slate-950/70 backdrop-blur-xl shrink-0">
        <MiraAvatar className="h-9 w-9" />
        <div>
          <div className="font-display font-bold leading-tight">Mira</div>
          <div className="text-xs text-emerald-400/90 leading-tight">Your growth advisor · online</div>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto mm-scroll">
        <div className="max-w-2xl mx-auto w-full px-4 sm:px-5 py-6 space-y-4" data-testid="chat-thread">
          {typing && (
            <div className="flex items-end gap-2 justify-start mm-fade-in">
              <MiraAvatar className="h-8 w-8 mb-1" />
              <div className="bg-slate-900 border border-slate-800 rounded-2xl rounded-bl-md px-4 py-2.5"><TypingDots /></div>
            </div>
          )}
          {thread.map((m, i) => (
            <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"} mm-fade-in`}>
              {m.role === "assistant" && <MiraAvatar className="h-8 w-8 mb-1" />}
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  m.role === "user"
                    ? "bg-emerald-500 text-slate-950 font-medium rounded-br-md"
                    : "bg-slate-900 border border-slate-800 text-slate-100 rounded-bl-md"
                }`}
                data-testid={m.role === "assistant" ? "mira-message" : "user-message"}
              >
                {m.content ? (
                  m.role === "assistant" ? (
                    <Markdown className="text-[15px] leading-relaxed" onOfferClick={trackOffer}>{m.content}</Markdown>
                  ) : (
                    <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{m.content}</div>
                  )
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-end gap-2 justify-start mm-fade-in" data-testid="chat-loading">
              <MiraAvatar className="h-8 w-8 mb-1" />
              <div className="bg-slate-900 border border-slate-800 rounded-2xl rounded-bl-md px-4 py-2.5"><TypingDots /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-slate-800 bg-slate-950/70 backdrop-blur-xl px-4 sm:px-5 py-3 shrink-0">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-2xl p-2 focus-within:border-emerald-500/60 transition-colors">
            {/* One persistent input for both the email gate and the chat, so focus (and the mobile
                keyboard) is never lost when the flow moves from email → conversation. */}
            <input
              data-testid={phase === "email" ? "chat-email-input" : "chat-input"}
              ref={inputRef}
              type="text"
              inputMode={phase === "email" ? "email" : "text"}
              autoComplete={phase === "email" ? "email" : "off"}
              autoCapitalize={phase === "email" ? "none" : "sentences"}
              enterKeyHint="send"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSubmit(); } }}
              onFocus={() => setTimeout(() => scrollEnd(false), 350)}
              placeholder={phase === "email" ? "you@yourschool.com" : "Message Mira…"}
              className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-base outline-none placeholder:text-slate-500"
            />
            <Button
              data-testid="chat-send"
              onPointerDown={(e) => e.preventDefault()}
              onClick={onSubmit}
              disabled={busy || typing || !input.trim()}
              className="h-11 w-11 p-0 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 shrink-0"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowUp className="h-5 w-5" strokeWidth={2.5} />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
